import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@copro/api';
import { _3_DAYS } from '../../../constants/durations';
import { useSpace } from '../../../hooks/useSpace';
import type { GoogleDriveConfig } from '../types/googleDriveConfig';
import { keys } from './keys';

async function fetchDriveConfig(spaceId: string): Promise<GoogleDriveConfig | null> {
  // Columns restricted at DB level: refresh_token / access_token are NOT granted
  // to `authenticated` role, so they are never fetched client-side.
  // Cast on `from` until Database types are regenerated (npm run apiClient:model)
  // after the 20260423_add_google_drive_configs migration is applied.
  const { data, error } = await (apiClient.from as unknown as (table: string) => ReturnType<typeof apiClient.from>)('google_drive_configs')
    .select('space_id, google_email, google_user_id, root_folder_id, root_folder_name, connected_by, created_at, updated_at')
    .eq('space_id', spaceId)
    .maybeSingle();

  if (error) {
    throw error;
  }

  return (data as unknown as GoogleDriveConfig | null) ?? null;
}

export default function useGetDriveConfig() {
  const { currentSpace } = useSpace();

  return useQuery({
    queryKey: currentSpace ? keys.config(currentSpace.id) : keys.all,
    queryFn: () => fetchDriveConfig(currentSpace!.id),
    enabled: !!currentSpace?.id,
    staleTime: _3_DAYS,
  });
}
